import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Flex,
  Popover,
  Skeleton,
  Tooltip,
  Typography,
  message,
  theme,
} from "antd";
import { EyeOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import { axiosInstance } from "@/utilities";
import { API_URL_LINKED_SEARCH } from "@/services/XCONS_ENV";
import { LinkedProductCard } from "@/components/xcons/cards/LinkedProductCard";

const { Text } = Typography;

/** Cuántas sugerencias se piden por producto: las que se recorren con las flechas. */
const CUANTAS = 5;

interface Sugerencias {
  opciones: any[];
  /** Cuál se muestra: las flechas y Descartar la mueven, la lista no cambia. */
  indice: number;
  /**
   * Sólo la sugerencia guardada, sin las otras ni los datos de la vista previa: se piden
   * recién si se usan las flechas, Descartar o el ojo (xcons-docs#624).
   */
  parcial?: boolean;
}

/**
 * La sugerencia guardada del producto (xcons-docs#624), para dibujar la fila sin buscar: una
 * página de 100 no dispara 100 búsquedas. `null` si no hay nada guardado (todavía no se
 * calculó, o la API no lo manda): la fila busca como antes.
 */
const sugerenciaGuardada = (producto: any): Sugerencias | null => {
  const m = producto?.match;
  if (!m) return null;
  if (!m.suggestedSku) return { opciones: [], indice: 0 };
  return {
    opciones: [
      { sku: m.suggestedSku, name: m.suggestedName, matchScore: m.score ?? 0 },
    ],
    indice: 0,
    parcial: true,
  };
};

/**
 * Las sugerencias de cada producto del seller, fuera de React: la tabla redibuja las filas
 * seguido y la acción masiva necesita leer la sugerencia actual de cada seleccionado sin
 * volver a buscarla. Se pierden al recargar, que es lo esperable para un Descartar.
 */
const memoria = new Map<string, Sugerencias>();
const clave = (sellerCode: string, sku: string) => `${sellerCode}/${sku}`;

/** Un nombre que el seller no cargó llega como "undefined": no hay con qué buscar. */
const sinNombre = (nombre?: string) =>
  !nombre || !nombre.trim() || nombre.trim() === "undefined";

/**
 * Las del catálogo que se parecen al producto del seller, la más compatible primero. El
 * buscador es el mismo del asociador y el % es su `matchScore`. Van también las que el seller
 * ya tiene asociadas a otro de sus productos (`vendorSku`): casi siempre es un SKU cargado dos
 * veces, y la que le corresponde. Asociarla lo explica la API (`catalogo_tomado`,
 * xcons-docs#631).
 */
export const pedirSugerencias = async (
  sellerCode: string,
  producto: any,
): Promise<Sugerencias> => {
  const k = clave(sellerCode, producto.sku);
  const guardadas = memoria.get(k);
  if (guardadas) return guardadas;
  let opciones: any[] = [];
  if (!sinNombre(producto.name)) {
    const query = new URLSearchParams({
      usePriceFilter: "false",
      searchQuery: producto.name,
      page: "1",
      limit: String(CUANTAS),
    });
    const { data } = await axiosInstance.get(
      `${API_URL_LINKED_SEARCH}/${sellerCode}/search/${encodeURIComponent(
        producto.sku,
      )}?${query}`,
    );
    opciones = (data?.data ?? []).sort((a: any, b: any) => (b.matchScore ?? 0) - (a.matchScore ?? 0));
    // La guardada va primero y con su % (xcons-docs#624): es la misma con que filtra el side
    // panel. Las demás se siguen pidiendo para recorrerlas y para la vista previa.
    const guardada = producto.match?.suggestedSku;
    const i = guardada ? opciones.findIndex((o) => o.sku === guardada) : -1;
    if (i >= 0) {
      const [opcion] = opciones.splice(i, 1);
      opciones.unshift({ ...opcion, matchScore: producto.match.score });
    }
  }
  const sugerencias = { opciones, indice: 0 };
  memoria.set(k, sugerencias);
  return sugerencias;
};

/** La sugerencia que se ve ahora para un producto, si ya se pidió y queda alguna. */
export const sugerenciaActual = (sellerCode: string, sku: string) => {
  const s = memoria.get(clave(sellerCode, sku));
  return s?.opciones[s.indice];
};

/** Por qué la API no asoció un par (`POST assign`, xcons-docs#611). */
const MOTIVOS: Record<string, string> = {
  repetido: "otro de los seleccionados ya se llevó ese producto del catálogo",
  no_existe: "el producto ya no existe",
  ya_asociado: "ya estaba asociado",
  catalogo_no_existe: "el producto del catálogo ya no existe",
  catalogo_tomado: "otro producto tuyo ya lo tiene",
  no_se_vende: "el producto del catálogo está pausado o archivado",
};

export interface ResultadoDeSugerencia {
  sellerSku: string;
  mainCatalogSku: string;
  ok: boolean;
  motivo?: string;
  asociadoA?: string;
  detalle?: string;
}

export const motivoSinAsociar = (r: ResultadoDeSugerencia) =>
  r.motivo === "catalogo_tomado" && r.asociadoA
    ? `${MOTIVOS.catalogo_tomado} (${r.asociadoA})`
    : MOTIVOS[r.motivo ?? ""] ?? r.detalle ?? "error";

/**
 * Asociar con la sugerencia, uno o varios (xcons-docs#611): una sola llamada, factor 1, y el %
 * que se mostraba queda en el Historial del producto. Devuelve qué se asoció y qué no, con su
 * motivo.
 */
export const asociarConSugerencias = async (
  sellerCode: string,
  pares: Array<{ sellerSku: string; catalogo: any }>,
): Promise<ResultadoDeSugerencia[]> => {
  const { data } = await axiosInstance.post(
    `${API_URL_LINKED_SEARCH}/${sellerCode}/assign`,
    {
      pares: pares.map(({ sellerSku, catalogo }) => ({
        sellerSku,
        mainCatalogSku: catalogo.sku,
        ...(typeof catalogo.matchScore === "number" && {
          matchScore: catalogo.matchScore,
        }),
      })),
    },
  );
  for (const r of data?.resultados ?? [])
    if (r.ok) memoria.delete(clave(sellerCode, r.sellerSku));
  return data?.resultados ?? [];
};

/** Asociar con una sugerencia: la de una fila. Si la API no la asocia, falla con el motivo. */
export const asociarConSugerencia = async (
  sellerCode: string,
  sellerSku: string,
  catalogo: any,
) => {
  const [r] = await asociarConSugerencias(sellerCode, [{ sellerSku, catalogo }]);
  if (!r?.ok) throw new Error(r ? motivoSinAsociar(r) : "error");
};

/** Verde desde 80%, amarillo desde 50%, rojo abajo: cuánto conviene mirarla antes. */
const colorDelPorcentaje = (score: number, token: any) =>
  score >= 0.8
    ? token.colorSuccess
    : score >= 0.5
    ? token.colorWarning
    : token.colorError;

/** Los cortes del semáforo, los mismos del filtro y del aviso del import (`score >= 0,8`). */
const CORTES = [0.5, 0.8];

/**
 * El % en entero, salvo cuando redondear lo pasaría al color de arriba: un 0,795 no dice "80%"
 * en amarillo, dice "79,5%". Ahí va con un decimal, truncado (0,7999 → 79,9%), para que el número
 * y el color digan lo mismo. La API guarda cuatro decimales: se cuenta en diezmilésimos.
 */
export const textoDelPorcentaje = (score = 0) => {
  const diezmilesimos = Math.round(score * 10000);
  const entero = Math.round(diezmilesimos / 100);
  const cruza = CORTES.some(
    (corte) => diezmilesimos < corte * 10000 && entero >= corte * 100,
  );
  if (!cruza) return `${entero}%`;
  return `${(Math.floor(diezmilesimos / 10) / 10).toFixed(1).replace(".", ",")}%`;
};

/** El % con un punto de color de semáforo adelante, como el "Completado" del historial. */
export const PorcentajeDeCompatibilidad: React.FC<{ score?: number }> = ({
  score,
}) => {
  const { token } = theme.useToken();
  const color = colorDelPorcentaje(score ?? 0, token);
  return (
    <Tooltip title="Compatibilidad con tu producto">
      <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: color,
            display: "inline-block",
          }}
        />
        <Text
          style={{ fontSize: 12, fontWeight: 500, color, whiteSpace: "nowrap" }}
        >
          {textoDelPorcentaje(score ?? 0)}
        </Text>
      </Flex>
    </Tooltip>
  );
};

interface SugerenciaDeAsociacionProps {
  sellerCode: string;
  producto: any;
  /** Después de asociar: la tabla se refresca y la fila pasa a asociada. */
  onAsociado?: () => void;
}

/**
 * La primera sugerencia del catálogo debajo del nombre de un producto sin asociar
 * (xcons-docs#609): con su % de compatibilidad, una vista previa con el ojo, y Asociar o
 * Descartar ahí mismo. Las flechas recorren las sugerencias sin descartar. Pide recién cuando
 * la fila se ve: una página de 100 no dispara 100 búsquedas juntas.
 */
export const SugerenciaDeAsociacion: React.FC<SugerenciaDeAsociacionProps> = ({
  sellerCode,
  producto,
  onAsociado,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const ref = useRef<HTMLDivElement>(null);
  const [sugerencias, setSugerencias] = useState<Sugerencias | null>(
    memoria.get(clave(sellerCode, producto.sku)) ??
      sugerenciaGuardada(producto),
  );
  const [completando, setCompletando] = useState(false);
  const [asociando, setAsociando] = useState(false);
  const [vistaAbierta, setVistaAbierta] = useState(false);

  useEffect(() => {
    if (sugerencias || !ref.current || sinNombre(producto.name)) return;
    const observer = new IntersectionObserver(
      ([entrada]) => {
        if (!entrada.isIntersecting) return;
        observer.disconnect();
        pedirSugerencias(sellerCode, producto)
          .then(setSugerencias)
          .catch(() => setSugerencias({ opciones: [], indice: 0 }));
      },
      { rootMargin: "200px" },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [sellerCode, producto.sku]);

  if (sinNombre(producto.name)) return null;

  const actual = sugerencias?.opciones[sugerencias.indice];
  const total = sugerencias?.opciones.length ?? 0;

  /** Cambiar el estado guarda también en la memoria, que lee la acción masiva. */
  const actualizar = (nuevo: Sugerencias) => {
    memoria.set(clave(sellerCode, producto.sku), nuevo);
    setSugerencias(nuevo);
    setVistaAbierta(false);
  };

  /** La lista entera, si hasta ahora sólo se tenía la guardada: la pide una vez. */
  const completar = async (): Promise<Sugerencias | null> => {
    if (!sugerencias?.parcial) return sugerencias;
    setCompletando(true);
    try {
      const completas = await pedirSugerencias(sellerCode, producto);
      setSugerencias(completas);
      return completas;
    } catch {
      return sugerencias;
    } finally {
      setCompletando(false);
    }
  };

  /** Las flechas recorren sin descartar: se puede ir y volver. */
  const mover = async (paso: number) => {
    const s = await completar();
    if (!s || s.parcial) return;
    const indice = Math.min(Math.max(s.indice + paso, 0), s.opciones.length - 1);
    actualizar({ ...s, indice });
  };

  /**
   * Descartar pasa a la siguiente, sin sacarla de la lista: el contador no se achica
   * (1/5, 2/5…). Después de la última vuelve a la primera.
   */
  const descartar = async () => {
    const s = await completar();
    if (!s || s.parcial || !s.opciones.length) return;
    actualizar({ ...s, indice: (s.indice + 1) % s.opciones.length });
  };

  /** La vista previa necesita la ficha entera, que la guardada no trae. */
  const abrirVista = async (abrir: boolean) => {
    if (!abrir) return setVistaAbierta(false);
    const s = await completar();
    if (s && !s.parcial) setVistaAbierta(true);
  };

  const asociar = async () => {
    if (!actual) return;
    setAsociando(true);
    try {
      await asociarConSugerencia(sellerCode, producto.sku, actual);
      message.success("Producto asociado");
      setVistaAbierta(false);
      onAsociado?.();
    } catch (e: any) {
      message.error(
        e?.message && !e?.isAxiosError
          ? `No se pudo asociar: ${e.message}`
          : "No se pudo asociar",
      );
    } finally {
      setAsociando(false);
    }
  };

  /** Asociar y Descartar como links: la sugerencia acompaña al nombre, no compite con él. */
  const acciones = (
    <>
      <Typography.Link
        onClick={asociar}
        disabled={asociando}
        style={{ fontSize: 12 }}
      >
        {asociando ? "Asociando…" : t("buttons.link")}
      </Typography.Link>
      <Typography.Link
        onClick={descartar}
        style={{ fontSize: 12, color: token.colorTextTertiary }}
      >
        Descartar
      </Typography.Link>
    </>
  );

  return (
    // Sin propagar el clic: la fila abre el asociador, y acá se decide sin abrirlo.
    <div
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      // El ancho de la columna Nombre: si no, un nombre largo no se corta y empuja el ojo afuera.
      style={{ maxWidth: 330 }}
    >
      {!sugerencias ? (
        <Skeleton.Input
          active
          size="small"
          style={{ width: 180, height: 16 }}
        />
      ) : !actual ? (
        // Nada parecido en el catálogo: un rojo más, como lo filtra el side panel (#631).
        <PorcentajeDeCompatibilidad score={0} />
      ) : (
        <Flex vertical gap={2}>
          <Flex align="center" gap={4} style={{ minWidth: 0 }}>
            <Text
              ellipsis={{ tooltip: actual.name }}
              strong
              // Como el nombre de uno asociado —verde y en negrita—, pero apagado: todavía no
              // lo está.
              style={{ color: token.colorPrimary, opacity: 0.5, minWidth: 0 }}
            >
              {actual.name}
            </Text>
            <Popover
              open={vistaAbierta}
              onOpenChange={abrirVista}
              trigger="click"
              placement="right"
              content={
                <Flex vertical gap={10} style={{ width: 240 }}>
                  <LinkedProductCard
                    t={t}
                    item={actual}
                    linkedID={actual.sku}
                    handleSelectProduct={() => undefined}
                  />
                </Flex>
              }
            >
              <EyeOutlined
                aria-label="Vista previa"
                style={{
                  fontSize: 13,
                  color: token.colorTextTertiary,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
            </Popover>
          </Flex>
          <Flex align="center" gap={12}>
            <PorcentajeDeCompatibilidad score={actual.matchScore} />
            {acciones}
            {/* Con sólo la guardada no se sabe cuántas hay: una flecha para pedir las demás. */}
            {sugerencias!.parcial && (
              <Button
                type="text"
                size="small"
                loading={completando}
                icon={<RightOutlined style={{ fontSize: 10 }} />}
                aria-label="Ver más sugerencias"
                onClick={() => mover(1)}
                style={{ width: 18, height: 18, minWidth: 18 }}
              />
            )}
            {!sugerencias!.parcial && total > 1 && (
              <Flex align="center" gap={2}>
                <Button
                  type="text"
                  size="small"
                  icon={<LeftOutlined style={{ fontSize: 10 }} />}
                  aria-label="Sugerencia anterior"
                  disabled={sugerencias!.indice === 0}
                  onClick={() => mover(-1)}
                  style={{ width: 18, height: 18, minWidth: 18 }}
                />
                <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
                  {sugerencias!.indice + 1}/{total}
                </Text>
                <Button
                  type="text"
                  size="small"
                  icon={<RightOutlined style={{ fontSize: 10 }} />}
                  aria-label="Sugerencia siguiente"
                  disabled={sugerencias!.indice >= total - 1}
                  onClick={() => mover(1)}
                  style={{ width: 18, height: 18, minWidth: 18 }}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </div>
  );
};
